import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import { TGetPrizesParams, TPrize } from '@/typings/api/instagram_campaign_prizes.type'

type CampaignPrizeState = {
  firstLoaded: boolean
  error: boolean
  total: number
  prizes: TPrize[]
  params: TGetPrizesParams
  campaign: TCampaign | null
}

const createInitialState = (): CampaignPrizeState => ({
  firstLoaded: false,
  error: false,
  campaign: null,
  prizes: [],
  total: 0,
  params: {
    campaignId: 0,
    sortBy: '-applicantCount',
    pagingNo: 1,
    limitCount: 25,
    forCsv: 0
  }
})

export const useInstagramCampaignPrizeStore = defineStore('instagramCampaignPrizeStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const firstLoaded = ref<CampaignPrizeState['firstLoaded']>(initialState.firstLoaded)
  const error = ref<CampaignPrizeState['error']>(initialState.error)
  const total = ref<CampaignPrizeState['total']>(initialState.total)
  const prizes = ref<CampaignPrizeState['prizes']>(initialState.prizes)
  const params = ref<CampaignPrizeState['params']>(initialState.params)
  const campaign = ref<CampaignPrizeState['campaign']>(initialState.campaign)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    firstLoaded.value = nextState.firstLoaded
    error.value = nextState.error
    total.value = nextState.total
    prizes.value = nextState.prizes
    params.value = nextState.params
    campaign.value = nextState.campaign
  }

  const fetchPrizes = async (): Promise<void> => {
    error.value = false
    await systemStore.setLoading(true)

    const result = await api.get('/instagram_campaign_prizes', {
      params: params.value
    })

    await systemStore.setLoading(false)
    firstLoaded.value = true

    if (!result.data) {
      error.value = true
      return
    }

    prizes.value = result.data.prizes
    total.value = result.data.total
  }

  const fetchCampaign = async (): Promise<void> => {
    const result = await api.get('/campaigns/' + params.value.campaignId)
    if (result.data) {
      campaign.value = result.data
    }
  }

  const setParams = (newParams: Partial<TGetPrizesParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  return {
    firstLoaded: readonly(firstLoaded),
    error: readonly(error),
    total: readonly(total),
    prizes: readonly(prizes),
    params: readonly(params),
    campaign: readonly(campaign),
    reset,
    fetchPrizes,
    fetchCampaign,
    setParams
  }
})
